/**
 * Slug helpers shared by project names (src/data/projects.ts) and document
 * file ids (src/data/documents.ts). A slug is a URL/path/cloud-safe
 * identifier: lowercase letters and digits in hyphen-separated groups.
 */

/**
 * Derive a slug from free text: lowercase, spaces/underscores → hyphens,
 * strip everything else, collapse and trim hyphens. Always returns a valid
 * slug, or "" if the input had no slug-safe characters.
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** True when `text` is a non-empty slug (no leading/trailing/double hyphens, no other chars). */
export function isSlug(text: string): boolean {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(text);
}

/** Import formats whose extension shouldn't leak into a derived file id — the document becomes a native in-app format regardless of source. */
const IMPORTED_DOCUMENT_EXTENSIONS = [".docx", ".pdf"];

/**
 * Slug helper for deriving a document's file id from its (possibly
 * filename-shaped) name. Strips a trailing .docx/.pdf first so an imported
 * "Interview 1.docx" slugs to "interview-1", not "interview-1docx" — the
 * dot would otherwise just be stripped by slugify() and fuse into the word.
 */
export function slugifyDocumentName(text: string): string {
  const lower = text.toLowerCase();
  const ext = IMPORTED_DOCUMENT_EXTENSIONS.find((e) => lower.endsWith(e));
  return slugify(ext ? text.slice(0, -ext.length) : text);
}

/**
 * Rough inverse of slugify for display: hyphens → spaces, each word
 * capitalized. "my-research-project" → "My Research Project". Lossy (original
 * casing and stripped punctuation can't be recovered) — only a friendly
 * fallback label when no human-entered one is available.
 */
export function unslug(slug: string): string {
  return slug
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Per-keystroke sanitizer for an editable slug field: keeps only slug-safe
 * characters (lowercase, digits, hyphen) without collapsing or trimming
 * hyphens, so the user can still type intermediate values like "my-" → "my-app".
 */
export function sanitizeSlugInput(value: string): string {
  return value
    .toLowerCase()
    .replace(/[\s_]+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}
